<template>
  <div class="stock-bollinger">
    <div class="page-header">
      <div class="page-title">
        <div class="title-icon">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 12H21M3 6H21M3 18H21M7 12L9 8L13 14L17 10L19 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h1>布林线分析</h1>
        <div class="title-decoration"></div>
      </div>
      <p class="page-subtitle">基于20日移动平均线和2倍标准差的波动分析系统</p>
    </div>

    <div class="filter-bar">
      <el-select
        v-model="selectedCode"
        placeholder="选择股票"
        filterable
        clearable
        class="filter-select"
        :loading="stockListLoading"
      >
        <el-option
          v-for="item in stockCodeList"
          :key="item.stock_code"
          :label="`${item.stock_name} (${item.stock_code})`"
          :value="item.stock_code"
        >
        </el-option>
      </el-select>

      <el-date-picker
        v-model="startDate"
        type="date"
        placeholder="开始日期"
        value-format="yyyy-MM-dd"
        class="date-picker"
      >
      </el-date-picker>

      <el-date-picker
        v-model="endDate"
        type="date"
        placeholder="结束日期"
        value-format="yyyy-MM-dd"
        class="date-picker"
      >
      </el-date-picker>

      <el-button type="primary" @click="fetchData" :loading="isQuerying">
        <i class="el-icon-search"></i>
        查询
      </el-button>
      <el-button @click="resetFilters">
        <i class="el-icon-refresh-left"></i>
        重置
      </el-button>
    </div>

    <!-- 单股票图表视图 -->
    <div v-if="selectedCode" class="chart-container">
      <!-- ECharts 图表 -->
      <div ref="bollingerChart" class="bollinger-chart"></div>

      <!-- 数据表格（可折叠） -->
      <el-collapse v-model="tableVisible" class="table-collapse">
        <el-collapse-item title="查看详细数据" name="table">
          <el-table
            :data="stockList"
            border
            stripe
            style="width: 100%"
            class="stock-table"
            :default-sort="{ prop: 'date', order: 'descending' }"
          >
            <el-table-column prop="date" label="日期" width="120"></el-table-column>
            <el-table-column prop="close" label="收盘价" width="100" align="right">
              <template slot-scope="scope">
                <span :class="getValueClass(scope.row.close, scope.row.mid_band)">
                  {{ scope.row.close }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="mid_band" label="中轨" width="100" align="right"></el-table-column>
            <el-table-column prop="upper_band" label="上轨" width="100" align="right">
              <template slot-scope="scope">
                <span class="value-high">{{ scope.row.upper_band }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="lower_band" label="下轨" width="100" align="right">
              <template slot-scope="scope">
                <span class="value-low">{{ scope.row.lower_band }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="percent_b" label="%B" width="100" align="right">
              <template slot-scope="scope">
                <span :class="getPercentBClass(scope.row.percent_b)">
                  {{ scope.row.percent_b ? Number(scope.row.percent_b).toFixed(2) : '-' }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="band_width" label="带宽%" width="100" align="right">
              <template slot-scope="scope">
                {{ scope.row.band_width ? Number(scope.row.band_width).toFixed(2) + '%' : '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="price_position" label="价格位置" width="120">
              <template slot-scope="scope">
                <el-tag :type="getStatusTagType(scope.row.price_position)" size="small">
                  {{ scope.row.price_position }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>
        </el-collapse-item>
      </el-collapse>
    </div>

    <!-- 空状态 -->
    <div v-else class="empty-state">
      <i class="el-icon-data-analysis"></i>
      <p>请选择股票开始分析</p>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import * as echarts from 'echarts'

export default {
  name: 'StockBollinger',
  data() {
    // 获取当前日期，格式化为 YYYY-MM-DD
    const today = new Date()
    const endDate = today.toISOString().split('T')[0]

    return {
      selectedCode: '',
      startDate: '2025-01-01',
      endDate: endDate,
      stockCodeList: [],
      stockListLoading: false,
      stockList: [],
      isQuerying: false,
      currentStockInfo: {},
      currentData: {},
      tableVisible: '',
      chart: null,
      storageKey: 'selected_stock_code'
    }
  },
  mounted() {
    this.loadStockCodeList()
    this.restoreSelectedCode()
  },
  watch: {
    // 监听路由参数变化，支持直接在URL输入股票代码
    '$route.query.code': {
      immediate: true,
      handler(newCode) {
        if (newCode && newCode !== this.selectedCode) {
          this.selectedCode = newCode
          // 更新localStorage
          localStorage.setItem(this.storageKey, newCode)
          // 获取数据
          this.fetchData()
        }
      }
    },
    selectedCode(newVal) {
      if (newVal) {
        localStorage.setItem(this.storageKey, newVal)
        // 同步更新URL
        if (this.$route.query.code !== newVal) {
          this.$router.replace({ query: { code: newVal } })
        }
      }
    }
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose()
    }
  },
  methods: {
    async loadStockCodeList() {
      this.stockListLoading = true
      try {
        const response = await axios.get('/stock/info/list')
        if (response.data.status === 'success') {
          this.stockCodeList = response.data.data
        }
      } catch (error) {
        this.$message.error('加载股票列表失败')
        console.error(error)
      } finally {
        this.stockListLoading = false
      }
    },

    restoreSelectedCode() {
      // 优先从URL参数读取，其次从localStorage读取
      const urlCode = this.$route.query.code
      const savedCode = localStorage.getItem(this.storageKey)
      const code = urlCode || savedCode

      if (code) {
        this.selectedCode = code
        // 同步更新URL和localStorage
        localStorage.setItem(this.storageKey, code)
        if (this.$route.query.code !== code) {
          this.$router.replace({ query: { code } })
        }
      }
    },

    async fetchData() {
      if (!this.selectedCode) {
        this.$message.warning('请选择股票')
        return
      }

      // 设置查询状态
      console.log('开始查询，设置 isQuerying = true')
      this.isQuerying = true

      try {
        const params = {}

        // 添加日期范围参数
        if (this.startDate) {
          params.start_date = this.startDate
        }
        if (this.endDate) {
          params.end_date = this.endDate
        }

        console.log('发送请求，参数:', params)
        const response = await axios.get(`/stock/bollinger/${this.selectedCode}`, {
          params
        })

        console.log('收到响应:', response.data)
        if (response.data.status === 'success') {
          this.stockList = response.data.data

          if (this.stockList.length > 0) {
            this.currentData = this.stockList[this.stockList.length - 1]
            this.currentStockInfo = this.stockCodeList.find(
              item => item.stock_code === this.selectedCode
            ) || {}
            this.$nextTick(() => {
              this.renderChart()
            })
          } else {
            this.$message.info('该股票暂无布林线数据')
          }
        } else {
          this.$message.error(response.data.message || '查询失败')
        }
      } catch (error) {
        this.$message.error('查询失败: ' + (error.message || '网络错误'))
        console.error('fetchData error:', error)
      } finally {
        // 确保查询状态被重置
        console.log('finally 块执行，设置 isQuerying = false，当前值:', this.isQuerying)
        this.isQuerying = false
        console.log('设置后 isQuerying =', this.isQuerying)
        // 强制更新视图
        this.$forceUpdate()
      }
    },

    renderChart() {
      if (!this.$refs.bollingerChart || this.stockList.length === 0) {
        return
      }

      if (this.chart) {
        this.chart.dispose()
      }

      this.chart = echarts.init(this.$refs.bollingerChart)

      const dates = this.stockList.map(item => item.date)
      const closeData = this.stockList.map(item => item.close)
      const midBandData = this.stockList.map(item => item.mid_band)
      const upperBandData = this.stockList.map(item => item.upper_band)
      const lowerBandData = this.stockList.map(item => item.lower_band)

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          },
          formatter: function(params) {
            let result = params[0].axisValue + '<br/>'
            params.forEach(param => {
              result += `${param.marker} ${param.seriesName}: ${param.value}<br/>`
            })
            return result
          }
        },
        legend: {
          data: ['收盘价', '上轨', '中轨', '下轨'],
          textStyle: {
            color: '#94a3b8'
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: dates,
          boundaryGap: false,
          axisLine: {
            lineStyle: {
              color: '#475569'
            }
          },
          axisLabel: {
            color: '#94a3b8'
          }
        },
        yAxis: {
          type: 'value',
          scale: true,
          axisLine: {
            lineStyle: {
              color: '#475569'
            }
          },
          axisLabel: {
            color: '#94a3b8'
          },
          splitLine: {
            lineStyle: {
              color: '#334155'
            }
          }
        },
        series: [
          {
            name: '上轨',
            type: 'line',
            data: upperBandData,
            smooth: true,
            lineStyle: {
              color: '#f87171',
              width: 1.5
            },
            showSymbol: false
          },
          {
            name: '中轨',
            type: 'line',
            data: midBandData,
            smooth: true,
            lineStyle: {
              color: '#fbbf24',
              width: 1.5
            },
            showSymbol: false
          },
          {
            name: '下轨',
            type: 'line',
            data: lowerBandData,
            smooth: true,
            lineStyle: {
              color: '#60a5fa',
              width: 1.5
            },
            showSymbol: false,
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(96, 165, 250, 0.1)' },
                  { offset: 1, color: 'rgba(96, 165, 250, 0.05)' }
                ]
              }
            }
          },
          {
            name: '收盘价',
            type: 'line',
            data: closeData,
            smooth: true,
            lineStyle: {
              color: '#22c55e',
              width: 2
            },
            itemStyle: {
              color: '#22c55e'
            },
            showSymbol: false
          }
        ],
        dataZoom: [
          {
            type: 'inside',
            start: 50,
            end: 100
          },
          {
            start: 50,
            end: 100,
            height: 20,
            bottom: 10,
            borderColor: 'transparent',
            textStyle: {
              color: '#94a3b8'
            },
            handleStyle: {
              color: '#6366f1'
            }
          }
        ]
      }

      this.chart.setOption(option)

      // 响应式调整
      window.addEventListener('resize', () => {
        this.chart && this.chart.resize()
      })
    },

    onStockChange() {
      // Only fetch data when a stock is selected, don't auto-fetch on change
      // Let user manually click the query button
    },

    resetFilters() {
      this.selectedCode = ''
      const today = new Date()
      const endDate = today.toISOString().split('T')[0]
      this.startDate = '2025-01-01'
      this.endDate = endDate
      this.stockList = []
      this.currentData = {}
      this.currentStockInfo = {}
      this.isQuerying = false
      if (this.chart) {
        this.chart.dispose()
        this.chart = null
      }
    },

    getValueClass(close, midBand) {
      if (!close || !midBand) return ''
      if (close > midBand) return 'value-positive'
      if (close < midBand) return 'value-negative'
      return 'value-neutral'
    },

    getPercentBClass(value) {
      if (!value && value !== 0) return ''
      const numValue = Number(value)
      if (numValue >= 80) return 'percent-overbought'
      if (numValue <= 20) return 'percent-oversold'
      if (numValue > 50) return 'percent-bullish'
      return 'percent-bearish'
    },

    getStatusTagType(status) {
      const typeMap = {
        '突破上轨': 'danger',
        '上轨附近': 'warning',
        '中上轨之间': 'success',
        '中下轨之间': 'info',
        '下轨附近': 'warning',
        '跌破下轨': 'danger',
        '其他': 'info'
      }
      return typeMap[status] || 'info'
    }
  }
}
</script>

<style scoped>
.stock-bollinger {
  padding: 24px;
  min-height: calc(100vh - 64px);
}

/* 页面标题 */
.page-header {
  text-align: center;
  margin-bottom: 32px;
}

.page-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 12px;
}

.title-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3);
}

.title-icon svg {
  width: 28px;
  height: 28px;
}

.page-title h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  background: linear-gradient(135deg, #a5b4fc 0%, #c4b5fd 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.title-decoration {
  width: 60px;
  height: 4px;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 2px;
}

.page-subtitle {
  margin: 0;
  font-size: 14px;
  color: #64748b;
  letter-spacing: 0.5px;
}

/* 筛选栏 */
.filter-bar {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 24px;
  padding: 20px 24px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3),
              inset 0 1px 0 rgba(255, 255, 255, 0.05);
  flex-wrap: wrap;
}

.filter-select {
  width: 300px;
}

.filter-bar :deep(.el-select .el-input__inner) {
  background: rgba(15, 23, 42, 0.6) !important;
  border: 1px solid rgba(99, 102, 241, 0.2) !important;
  color: #e2e8f0 !important;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.filter-bar :deep(.el-select .el-input__inner):hover {
  border-color: rgba(99, 102, 241, 0.4) !important;
  box-shadow: 0 0 12px rgba(99, 102, 241, 0.15) !important;
}

.filter-bar :deep(.el-select__placeholder) {
  color: #64748b;
}

.filter-bar :deep(.el-date-picker) {
  width: 200px;
}

.filter-bar :deep(.el-date-picker .el-input__inner) {
  background: rgba(15, 23, 42, 0.6) !important;
  border: 1px solid rgba(99, 102, 241, 0.2) !important;
  color: #e2e8f0 !important;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.filter-bar :deep(.el-date-picker .el-input__inner):hover {
  border-color: rgba(99, 102, 241, 0.4) !important;
  box-shadow: 0 0 12px rgba(99, 102, 241, 0.15) !important;
}

.filter-bar :deep(.el-date-picker .el-input__prefix) {
  color: rgba(99, 102, 241, 0.6) !important;
}

.filter-bar :deep(.el-date-picker .el-input__icon) {
  color: rgba(99, 102, 241, 0.6) !important;
}
</style>

<style scoped>
/* 页面标题 */
.page-header {
  text-align: center;
  margin-bottom: 32px;
}

.page-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 12px;
}

.title-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3);
}

.title-icon svg {
  width: 28px;
  height: 28px;
}

.page-title h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  background: linear-gradient(135deg, #a5b4fc 0%, #c4b5fd 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.title-decoration {
  width: 60px;
  height: 4px;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 2px;
}

.page-subtitle {
  margin: 0;
  font-size: 14px;
  color: #64748b;
  letter-spacing: 0.5px;
}

/* 筛选栏 */
.filter-bar {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 24px;
  padding: 20px 24px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3),
              inset 0 1px 0 rgba(255, 255, 255, 0.05);
  flex-wrap: wrap;
}

.filter-select {
  width: 300px;
}

.filter-bar :deep(.el-select .el-input__inner) {
  background: rgba(15, 23, 42, 0.6) !important;
  border: 1px solid rgba(99, 102, 241, 0.2) !important;
  color: #e2e8f0 !important;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.filter-bar :deep(.el-select .el-input__inner):hover {
  border-color: rgba(99, 102, 241, 0.4) !important;
  box-shadow: 0 0 12px rgba(99, 102, 241, 0.15) !important;
}

.filter-bar :deep(.el-select__placeholder) {
  color: #64748b;
}

.filter-bar :deep(.el-date-picker) {
  width: 200px;
}

.filter-bar :deep(.el-date-picker .el-input__inner) {
  background: rgba(15, 23, 42, 0.6) !important;
  border: 1px solid rgba(99, 102, 241, 0.2) !important;
  color: #e2e8f0 !important;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.filter-bar :deep(.el-date-picker .el-input__inner):hover {
  border-color: rgba(99, 102, 241, 0.4) !important;
  box-shadow: 0 0 12px rgba(99, 102, 241, 0.15) !important;
}

.filter-bar :deep(.el-date-picker .el-input__prefix) {
  color: rgba(99, 102, 241, 0.6) !important;
}

.filter-bar :deep(.el-date-picker .el-input__icon) {
  color: rgba(99, 102, 241, 0.6) !important;
}

/* 图表容器 */
.chart-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.chart-header {
  padding: 24px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.stock-info h2 {
  margin: 0 0 16px 0;
  font-size: 24px;
  color: #e2e8f0;
}

.latest-data {
  display: flex;
  gap: 24px;
  align-items: center;
  flex-wrap: wrap;
}

.data-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.data-item label {
  color: #64748b;
  font-size: 14px;
}

.data-item strong {
  font-size: 16px;
  color: #e2e8f0;
}

.bollinger-chart {
  height: 500px;
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 16px;
  padding: 20px;
}

/* 折叠面板 */
.table-collapse {
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 16px;
}

.table-collapse :deep(.el-collapse-item__header) {
  color: #e2e8f0;
  background: transparent;
  border-bottom: 1px solid rgba(99, 102, 241, 0.1);
}

.table-collapse :deep(.el-collapse-item__wrap) {
  background: transparent;
}

/* 筛选结果 */
.filter-results {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.stock-count {
  padding: 12px 24px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 14px;
  color: #94a3b8;
}

.stock-count strong {
  color: #a5b4fc;
  font-size: 16px;
}

/* 表格样式 */
.stock-table {
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 12px;
  overflow: hidden;
}

.stock-table :deep(.el-table__header-wrapper) {
  background: rgba(15, 23, 42, 0.5);
}

.stock-table :deep(th.el-table__cell) {
  background: rgba(15, 23, 42, 0.8) !important;
  color: #a5b4fc;
  font-weight: 600;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
}

.stock-table :deep(td.el-table__cell) {
  border-bottom: 1px solid rgba(99, 102, 241, 0.1);
}

.stock-table :deep(.el-table__row) {
  background: rgba(30, 41, 59, 0.3);
}

.stock-table :deep(.el-table__row:hover td) {
  background: rgba(99, 102, 241, 0.1) !important;
}

/* 数值样式 */
.value-positive {
  color: #22c55e;
  font-weight: 600;
}

.value-negative {
  color: #ef4444;
  font-weight: 600;
}

.value-neutral {
  color: #94a3b8;
}

.value-high {
  color: #f87171;
  font-weight: 600;
}

.value-low {
  color: #60a5fa;
  font-weight: 600;
}

/* %B指标样式 */
.percent-overbought {
  color: #ef4444;
  font-weight: 700;
}

.percent-oversold {
  color: #22c55e;
  font-weight: 700;
}

.percent-bullish {
  color: #fbbf24;
  font-weight: 600;
}

.percent-bearish {
  color: #94a3b8;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 120px 24px;
  color: #64748b;
}

.empty-state i {
  font-size: 64px;
  margin-bottom: 16px;
  display: block;
}

.empty-state p {
  font-size: 16px;
  margin: 0;
}
</style>

<style>
/* 日期选择器面板深色主题覆盖 - 非scoped样式以确保生效 */
.el-picker-panel {
  background: rgba(15, 23, 42, 0.98) !important;
  border: 1px solid rgba(99, 102, 241, 0.3) !important;
}

.el-picker-panel * {
  color: #e2e8f0 !important;
}

.el-date-range-picker {
  background: rgba(15, 23, 42, 0.98) !important;
}

.el-date-range-picker__content {
  background: transparent !important;
}

.el-date-range-picker__header {
  background: transparent !important;
  border-bottom: 1px solid rgba(99, 102, 241, 0.2) !important;
}

.el-date-range-picker__header button {
  color: #94a3b8 !important;
  background: transparent !important;
}

.el-date-range-picker__header button:hover {
  color: #a5b4fc !important;
}

.el-date-table th {
  background: transparent !important;
  color: #94a3b8 !important;
  border-bottom: 1px solid rgba(99, 102, 241, 0.1) !important;
}

.el-date-table td .el-date-table-cell__text {
  color: #e2e8f0 !important;
}

.el-date-table td.today .el-date-table-cell__text {
  color: #6366f1 !important;
}

.el-date-table td.available:hover .el-date-table-cell__text {
  background: rgba(99, 102, 241, 0.15) !important;
}

.el-date-table td.in-range .el-date-table-cell__text {
  background: rgba(99, 102, 241, 0.15) !important;
}

.el-date-table td.start-date .el-date-table-cell__text,
.el-date-table td.end-date .el-date-table-cell__text {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%) !important;
  color: #fff !important;
}

.el-date-table td.disabled .el-date-table-cell__text {
  color: #475569 !important;
}

.el-picker-panel__footer {
  background: rgba(15, 23, 42, 0.8) !important;
  border-top: 1px solid rgba(99, 102, 241, 0.1) !important;
}

.el-picker-panel__link-btn {
  color: #94a3b8 !important;
  background: transparent !important;
}

.el-picker-panel__link-btn:hover {
  color: #a5b4fc !important;
}
</style>
